import { BottomBanner } from '@chiroup/components/BottomBanner';
import { createUSAePayMessageListener } from '@chiroup/core/functions/usaePayUtils';
import { useScript } from '@chiroup/hooks/useScript';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { LoadingOverlayProvider } from './components/common/LoadingOverlay';
import { PortalErrorBoundary } from './components/common/PortalErrorBoundary';
import { KioskShell } from './components/kiosk/KioskEntry';
import { NotFoundSchedule } from './components/NotFoundSchedule';
import PublicSchedule from './components/PublicSchedule';
import PublicRequest from './components/request/PublicRequest';
import { ShortRequestRedirect } from './components/request/ShortRequestRedirect';
import RootLanding from './components/RootLanding';
import Survey from './components/Survey';
import SurveyUnsubscribe from './components/SurveyUnsubscribe';

// `schedule.*` is a single-purpose host that only serves the public
// clinic-booking flow. Everything else (portal, kiosk, surveys,
// requests) lives on the main `healthcom.io` / `app.healthcom.io`
// hostnames. One bundle ships to both; route tree branches at runtime.
const isScheduleHost = window.location.hostname.startsWith('schedule.');

const PAY_JS_URL = import.meta.env.VITE_PAY_JS_URL as string;

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient();

function App() {
  const scriptStatus = useScript(PAY_JS_URL, {
    removeOnUnmount: false,
  });

  useEffect(() => {
    if (scriptStatus === 'ready') {
      // Guard: the PayJS script may load successfully but fail to define
      // window.usaepay (e.g. due to browser compatibility issues in Safari 26+).
      if (!(window as any).usaepay?.PaymentCard?.prototype) {
        return;
      }

      // Skip if the monkey patch was already applied (idempotency guard).
      if ((window as any).usaepay.PaymentCard.prototype._monkeyPatched) {
        return;
      }

      // Monkey patch the payJS object to update the addEventListner function
      (window as any).usaepay.PaymentCard.prototype.addEventListener = function (
        eventType: any,
        response: any
      ) {
        const messageListener = createUSAePayMessageListener(eventType, response);

        // Add the event listener
        window.addEventListener('message', messageListener);

        // Cleanup: Remove the event listener when the component unmounts
        return function cleanup() {
          window.removeEventListener('message', messageListener);
        };
      };

      // Mark as patched to avoid re-patching on re-renders.
      (window as any).usaepay.PaymentCard.prototype._monkeyPatched = true;
    } else if (scriptStatus === 'error') {
      // Handle script loading error here if needed.
    }
  }, [scriptStatus]);

  return (
    <QueryClientProvider client={queryClient}>
      <LoadingOverlayProvider>
        <BrowserRouter>
          <SentryRoutes>
            {isScheduleHost ? (
              <>
                {/*
                 * `schedule.*` tree — single-purpose host for the
                 * public clinic-booking flow.
                 *
                 * Canonical URL: `schedule.healthcom.io/<clinicPath>`.
                 * `/clinic-schedule/<clinicPath>` is kept as a working
                 * alias for any legacy/internal links that used the
                 * longer form. Anything else 404s — no portal, kiosk,
                 * survey, or request flows live on this host (they're
                 * on `healthcom.io` / `app.healthcom.io`).
                 */}
                {/*
                 * Splat matcher so sub-paths stay inside the booking
                 * shell — after a successful booking Schedule navigates
                 * to `/<clinicPath>/thank-you` (clinics key Google Ads
                 * conversion tracking off that page view), and a hard
                 * 404 here would break refresh/deep links to it.
                 */}
                <Route path="/:clinicPath/*" element={<PublicSchedule />} />
                <Route path="/clinic-schedule/:clinicPath/*" element={<PublicSchedule />} />
                <Route path="*" element={<NotFoundSchedule />} />
              </>
            ) : (
              <>
                {/*
                 * Public booking flow — same component the `schedule.*`
                 * tree above uses, mounted here under the explicit
                 * `/clinic-schedule/<path>` URL. The short single-
                 * segment form is intentionally NOT mounted on this
                 * host: on `healthcom.io` / `app.healthcom.io` the
                 * portal lives at `/*` (RootLanding) and a `:clinicPath`
                 * matcher would shadow real top-level paths.
                 */}
                <Route path="/clinic-schedule/:clinicPath/*" element={<PublicSchedule />} />
                {/*
                 * Public PatientRequest flow — moved over from
                 * healthcom-app. Owns its own validationCode-based auth
                 * state machine (RequestAuth → RequestCodeInput →
                 * RequestRunner) and renders independently of the
                 * authed portal shell.
                 */}
                <Route
                  path="/requests/:id/*"
                  element={
                    <PortalErrorBoundary boundary="request">
                      <PublicRequest />
                    </PortalErrorBoundary>
                  }
                />
                <Route path="/r/:code" element={<ShortRequestRedirect />} />
                {/*
                 * Kiosk + Terminal — shared-device check-in flow. Auths
                 * via a Request ID + verification code (no Cognito),
                 * then mounts the runner with `kiosk={true}` so uploads
                 * are filtered out and exit returns to the entry
                 * screen.
                 */}
                <Route path="/kiosk/*" element={<KioskShell />} />
                <Route path="/terminal/*" element={<KioskShell />} />
                {/*
                 * Post-care survey — fcid-tokenized URL emailed by the
                 * 30-day cron. Must work for any visitor regardless of
                 * portal/request/kiosk session, so it's mounted above
                 * the RootLanding catch-all and posts to the public
                 * `api-noauth` /survey endpoint.
                 *
                 * `/survey/unsubscribe` is linked from the review-link
                 * and 30-day-survey SES templates. `/email-subscription`
                 * is linked from appointment-reminder emails
                 * (`createNotificationEmail.ts`). Both are public,
                 * no-auth, and toggle `Patient.optOutEmail` via the
                 * `/subscription-status` endpoints — the alias keeps
                 * existing email links working without a server-side
                 * template change. Listed before `/survey` since React
                 * Router v6 matches exact paths, but kept adjacent so the
                 * survey-related routes stay together.
                 */}
                <Route path="/survey/unsubscribe" element={<SurveyUnsubscribe />} />
                <Route path="/email-subscription" element={<SurveyUnsubscribe />} />
                <Route path="/survey" element={<Survey />} />
                {/*
                 * Everything else goes through RootLanding. It handles
                 * login, clinic picking, and (once authed with a
                 * clinic) renders Container inline so the URL stays
                 * wherever the user is.
                 */}
                <Route path="/*" element={<RootLanding />} />
              </>
            )}
          </SentryRoutes>
        </BrowserRouter>
        {import.meta.env.VITE_STAGE === 'sandbox' && (
          <BottomBanner message="You are in sandbox mode. Do not use real data." />
        )}
      </LoadingOverlayProvider>
    </QueryClientProvider>
  );
}

export default App;
